import type { UseChatHelpers } from "@ai-sdk/react";
import { useHandler } from "@redotech/react-util/hook";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { AI_THINKING_PHRASES } from "@redotech/redo-web/ai/thinking-phrases";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { NOTIFICATION_SOUND } from "@redotech/redo-web/sound/sounds";
import { Text } from "@redotech/redo-web/text";
import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { settleToolPart } from "../conversion/settle-tool-part";
import { ChatMessagesRefProvider } from "../escalation/chat-messages-context";
import { useEscalationAuthor } from "../escalation/escalation-author-context";
import { isHumanEscalationMessage } from "../escalation/intercom-to-uimessage";
import type { AskRedoUIMessage } from "../types";
import { AskRedoAiStarSpinner } from "./ask-redo-ai-star-spinner";
import * as styles from "./ask-redo-conversation.module.css";
import { AskRedoHandoffDivider } from "./ask-redo-handoff-divider";
import { AskRedoMessage } from "./ask-redo-message";
import { AskRedoPinnedReferencesRow } from "./ask-redo-pinned-references-row";
import { isToolHidden } from "./tool-summaries";

/**
 * Scrollable conversation view. Patterned on AI Elements'
 * `<Conversation>` — follows new content as it streams in, but only
 * when the user is already pinned near the bottom (scroll up to
 * re-read older content and the view stops yanking you back down).
 *
 * Accessibility:
 *   - `role="log"` on the scroller marks the live conversation.
 *   - We intentionally do NOT put `aria-live` on the scroller
 *     itself: that would make screen readers re-announce every
 *     streamed text-delta and tool-state flip. Instead, a separate
 *     visually-hidden polite announcer surfaces a debounced
 *     "Assistant replied: <last sentence>" once the message lands.
 */
export interface AskRedoConversationProps {
  chat: UseChatHelpers<AskRedoUIMessage>;
  /** Comes from `useAskRedoChat` — passed in rather than re-derived
   *  here so there's one source of truth for the streaming flag. */
  isStreaming: boolean;
  emptyState?: React.ReactNode;
  /** Enables scroll restoration across Page-shell remounts (agent link
   *  card navigation). Omit to always open at the bottom. */
  threadId?: string;
}

interface ConversationScrollSnapshot {
  scrollTop: number;
  snappedUserMsgId: string | null;
  spacerMinHeight: string;
}

/** Page-shell remounts recreate this component with fresh refs; without a
 *  snapshot the first paint bottom-snaps and the pinned-prompt view is lost. */
const scrollSnapshotsByThread = new Map<string, ConversationScrollSnapshot>();

export const AskRedoConversation = memo(function AskRedoConversation({
  chat,
  isStreaming,
  emptyState,
  threadId,
}: AskRedoConversationProps) {
  const {
    messages,
    addToolApprovalResponse,
    error,
    regenerate,
    sendMessage,
    setMessages,
  } = chat;
  const { name: escalationAgentName } = useEscalationAuthor();

  // Stable text-send seam for interactive tool cards (the
  // present-image-options carousel sends a user turn on click).
  const handleToolSendMessage = useCallback(
    (text: string) => {
      void sendMessage({ text });
    },
    [sendMessage],
  );
  const handleToolSettled = useCallback(
    (toolCallId: string, output: unknown) => {
      setMessages((currentMessages) =>
        settleToolPart(currentMessages, toolCallId, output),
      );
    },
    [setMessages],
  );
  const handleRetry = useHandler(() => {
    void regenerate();
  });
  const { messagePins, attachPinsToMessage } = useContext(GlobalAgentContext);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnce = useRef(false);
  // Ref set to the latest user-message element so we can snap it to
  // the top of the viewport when the user submits a follow-up prompt.
  const lastUserMsgRef = useRef<HTMLDivElement>(null);
  // Tracks the id of the user message we most recently snapped to,
  // so we don't re-snap during the AI streaming response that follows.
  const snappedUserMsgIdRef = useRef<string | null>(null);
  // Spacer below the last message. Snap-to-top only works if there's enough
  // scrollable room beneath the new user prompt to lift it to the viewport
  // top; a freshly-sent prompt has nothing below it. This reserves exactly
  // the missing room and shrinks back to 0 as the reply fills in below.
  const spacerRef = useRef<HTMLDivElement>(null);

  // Save scroll state per thread on unmount so a remount restores the same
  // view. Nodes are captured at mount — the refs are gone by cleanup time.
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const spacer = spacerRef.current;
    return () => {
      const id = threadIdRef.current;
      if (!id || !scroller || !hasScrolledOnce.current) {
        return;
      }
      scrollSnapshotsByThread.set(id, {
        scrollTop: scroller.scrollTop,
        snappedUserMsgId: snappedUserMsgIdRef.current,
        spacerMinHeight: spacer?.style.minHeight ?? "0px",
      });
    };
  }, []);

  const adjustBottomSpacer = useCallback(() => {
    const scroller = scrollerRef.current;
    const spacer = spacerRef.current;
    if (!scroller || !spacer) {
      return;
    }
    const lastUserMsg = lastUserMsgRef.current;
    if (!lastUserMsg) {
      spacer.style.minHeight = "0px";
      return;
    }
    // `spacer.offsetTop` is where real content ends (independent of the
    // spacer's own height), so this is stable and converges in one pass.
    const contentBelowPrompt = spacer.offsetTop - lastUserMsg.offsetTop;
    const needed = Math.max(0, scroller.clientHeight - contentBelowPrompt);
    spacer.style.minHeight = `${needed}px`;
  }, []);

  // Drain any pending pin snapshot onto a freshly-appended user
  // message. The prompt input calls `snapshotPinsForNextMessage()` in
  // its handleSend right before triggering the AI SDK's async append,
  // so by the time React commits the new `messages` here, the snapshot
  // is waiting for its message id. We watch for length growth where
  // the new tail is `role: "user"` and claim it. `attachPinsToMessage`
  // is a no-op if nothing's pending, so this is safe to fire on every
  // messages change.
  const prevMessageCountRef = useRef(messages.length);
  useEffect(() => {
    const prev = prevMessageCountRef.current;
    if (messages.length > prev) {
      const newest = messages[messages.length - 1];
      if (newest?.role === "user") {
        attachPinsToMessage(newest.id);
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, attachPinsToMessage]);

  // Scroll behaviour on new content:
  //
  // - First load / history hydration: snap to the bottom so the merchant
  //   sees the latest exchange, not the oldest message.
  // - New user prompt: reserve room below it (the spacer) and smooth-snap
  //   it to the top so the reply streams in underneath — like ChatGPT.
  // - AI streaming: no auto-scroll; the reply fills in below the pinned
  //   prompt. We only keep the spacer sized as it grows.
  //
  // useLayoutEffect (not useEffect) so the scroll is applied against the
  // just-committed layout, before paint.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const bottom = bottomRef.current;
    if (!scroller || !bottom) {
      return;
    }
    // Nothing to scroll to yet — keep `hasScrolledOnce` false so the
    // first populated render is still treated as the initial snap.
    if (messages.length === 0) {
      return;
    }

    const isFirstScroll = !hasScrolledOnce.current;
    if (isFirstScroll) {
      const snapshot = threadId
        ? scrollSnapshotsByThread.get(threadId)
        : undefined;
      if (snapshot && spacerRef.current) {
        // Remounted thread we were just viewing: rebuild the exact view
        // (spacer first — scrollTop depends on it) instead of bottom-snapping.
        spacerRef.current.style.minHeight = snapshot.spacerMinHeight;
        scroller.scrollTop = snapshot.scrollTop;
        snappedUserMsgIdRef.current = snapshot.snappedUserMsgId;
      } else {
        // First paint: snap to the bottom so history loads at the end.
        bottom.scrollIntoView({ behavior: "auto" });
      }
      hasScrolledOnce.current = true;
      return;
    }

    // Detect a new last *user* message we haven't snapped to yet. We look for
    // the last user message rather than the last message overall, because an
    // empty assistant placeholder usually trails it the moment streaming
    // begins — checking only `messages[last].role` would miss the snap.
    let lastUserMessage: AskRedoUIMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const candidate = messages[i];
      if (candidate?.role === "user") {
        lastUserMessage = candidate;
        break;
      }
    }
    const lastUserMsg = lastUserMsgRef.current;
    if (
      lastUserMessage &&
      lastUserMessage.id !== snappedUserMsgIdRef.current &&
      lastUserMsg
    ) {
      // Reserve room below the prompt first, then animate it up to the top
      // so the prompt visibly glides into place (instead of a hard jump).
      adjustBottomSpacer();
      lastUserMsg.scrollIntoView({ behavior: "smooth", block: "start" });
      snappedUserMsgIdRef.current = lastUserMessage.id;
      return;
    }

    // Otherwise (AI streaming): keep the spacer sized to the growing reply
    // but DO NOT auto-scroll. The reply fills in below the pinned prompt —
    // auto-scroll happens only on send, ChatGPT-style.
    adjustBottomSpacer();
  }, [messages, adjustBottomSpacer, threadId]);

  // Chime when Ask Redo finishes responding. The transition we care
  // about is `isStreaming` going from true to false — that's when the
  // model just stopped streaming a reply. Browsers can reject `.play()`
  // when the page hasn't been interacted with yet; by the time the user
  // has sent a message in chat they have interacted, so the catch is a
  // safety net for the rare edge case (and silent failure is correct
  // for a chime).
  const wasStreamingRef = useRef(isStreaming);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      NOTIFICATION_SOUND.currentTime = 0;
      void NOTIFICATION_SOUND.play().catch(() => {});
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Suppress a trailing empty assistant message during streaming so
  // the thinking indicator doesn't sit underneath an invisible 0-height
  // row (which used to cause a vertical hop when the first delta
  // arrived). The full `messages` array still drives `shouldShowThinking`.
  const visibleMessages = useMemo(() => {
    const last = messages[messages.length - 1];
    if (
      isStreaming &&
      last &&
      last.role === "assistant" &&
      !hasMeaningfulContent(last)
    ) {
      return messages.slice(0, -1);
    }
    return messages;
  }, [messages, isStreaming]);

  if (messages.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  const lastUserMsgIdx = visibleMessages.findLastIndex(
    (m) => m.role === "user",
  );

  return (
    <ChatMessagesRefProvider messages={messages}>
      <Flex
        aria-label="Ask Redo conversation"
        className={styles.scroller}
        dir="column"
        gap="md"
        ref={scrollerRef}
        role="log"
      >
        {visibleMessages.map((message, idx) => {
          // Hide actions on the in-progress assistant message — only
          // show thumbs/copy once a reply is finalized. The currently-
          // streaming message is always the last entry in
          // `visibleMessages` (we already stripped a trailing empty
          // assistant placeholder upstream).
          const isLast = idx === visibleMessages.length - 1;
          const showActions = !(isStreaming && isLast);
          const disableQuestionResponses = hasLaterUserTurn(
            visibleMessages,
            idx,
          );
          const pinsForMessage =
            message.role === "user" ? messagePins.get(message.id) : undefined;
          const isLastUserMsg =
            message.role === "user" && lastUserMsgIdx === idx;
          const isFirstHumanEscalationMessage =
            isHumanEscalationMessage(message) &&
            !visibleMessages
              .slice(0, idx)
              .some((earlierMessage) =>
                isHumanEscalationMessage(earlierMessage),
              );
          return (
            <Fragment key={message.id}>
              {isFirstHumanEscalationMessage && (
                <AskRedoHandoffDivider
                  agentName={message.metadata?.agentName ?? escalationAgentName}
                />
              )}
              {pinsForMessage && pinsForMessage.length > 0 && (
                <AskRedoPinnedReferencesRow
                  pins={pinsForMessage}
                  variant="historical"
                />
              )}
              {isLastUserMsg ? (
                <Flex dir="column" ref={lastUserMsgRef}>
                  <AskRedoMessage
                    addToolApprovalResponse={addToolApprovalResponse}
                    disableQuestionResponses={disableQuestionResponses}
                    isStreaming={isStreaming}
                    message={message}
                    onSendMessage={handleToolSendMessage}
                    onToolSettled={handleToolSettled}
                    showActions={showActions}
                    threadId={threadId}
                  />
                </Flex>
              ) : (
                <AskRedoMessage
                  addToolApprovalResponse={addToolApprovalResponse}
                  disableQuestionResponses={disableQuestionResponses}
                  isStreaming={isStreaming}
                  message={message}
                  onSendMessage={handleToolSendMessage}
                  onToolSettled={handleToolSettled}
                  showActions={showActions}
                  threadId={threadId}
                />
              )}
            </Fragment>
          );
        })}
        {isStreaming && shouldShowThinking(messages) && <ThinkingIndicator />}
        {error && (
          <Flex
            align="center"
            className={styles.errorBanner}
            gap="md"
            justify="space-between"
          >
            <Text fontSize="sm" textColor="secondary">
              Something went wrong. {error.message}
            </Text>
            <RedoButton
              disabled={isStreaming}
              hierarchy="secondary"
              onClick={handleRetry}
              size="sm"
              text="Try again"
            />
          </Flex>
        )}
        <div ref={bottomRef} />
        {/* Reserves room so a freshly-sent prompt can snap to the top;
            sized imperatively in `adjustBottomSpacer`, shrinks to 0 as the
            reply fills the viewport. */}
        <Flex aria-hidden ref={spacerRef} />
      </Flex>
      <AssistantAnnouncer isStreaming={isStreaming} messages={messages} />
    </ChatMessagesRefProvider>
  );
});

/** Show thinking while the user is waiting for the assistant to say
 *  something *visible*. Two states qualify as waiting:
 *    1. Last message is user — haven't started replying.
 *    2. Last message is assistant but has no meaningful parts yet.
 *  Once any text/reasoning/tool part lands, the tool group's own
 *  inline cue takes over, so we drop the top-level indicator. */
function shouldShowThinking(messages: readonly AskRedoUIMessage[]): boolean {
  const last = messages[messages.length - 1];
  if (!last) {
    return true;
  }
  if (last.role === "user") {
    return true;
  }
  if (last.role !== "assistant") {
    return false;
  }
  return !hasMeaningfulContent(last);
}

function hasMeaningfulContent(message: AskRedoUIMessage): boolean {
  if (!message.parts || message.parts.length === 0) {
    return false;
  }
  return message.parts.some((part) => {
    if (part.type === "text") {
      return part.text.length > 0;
    }
    if (part.type === "reasoning") {
      return part.text.length > 0;
    }
    // Tool parts — `dynamic-tool` is what our transport produces
    // (no static tools map) and `tool-*` is what AI SDK uses for
    // statically-typed tools (kept for forward compat).
    //
    // Hidden tools (load_skill, knowledge-base searches, catalog
    // pre-flights) are filtered out by `AskRedoToolGroup` and render
    // nothing. If the assistant's ONLY parts are hidden tools, the
    // chat would otherwise look blank — no rows, and the thinking
    // indicator suppressed because tool parts technically exist.
    // Treat hidden tools as "not yet meaningful" so the thinking
    // indicator keeps showing until a visible tool or text lands.
    if (part.type === "dynamic-tool") {
      return !isToolHidden(part.toolName);
    }
    if (part.type.startsWith("tool-")) {
      // Static tools — not used by Ask Redo today, but visible by
      // default since they're explicitly registered.
      return true;
    }
    return false;
  });
}

function hasLaterUserTurn(
  messages: readonly AskRedoUIMessage[],
  messageIndex: number,
): boolean {
  return messages
    .slice(messageIndex + 1)
    .some((message) => message.role === "user");
}

/**
 * Visually-hidden polite live region. Announces the assistant's
 * latest reply once after streaming ends (and a short summary while
 * streaming is in progress), instead of re-announcing every chunk.
 * Screen readers do not see the noisy live conversation scroller.
 */
const ANNOUNCE_PREVIEW_CHARS = 240;

const AssistantAnnouncer = memo(function AssistantAnnouncer({
  messages,
  isStreaming,
}: {
  messages: readonly AskRedoUIMessage[];
  isStreaming: boolean;
}) {
  const announcement = useMemo(() => {
    if (isStreaming) {
      return "Assistant is responding…";
    }
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === "assistant") {
        return summarizeAssistant(m);
      }
    }
    return "";
  }, [messages, isStreaming]);

  return (
    <div
      aria-atomic="true"
      aria-live="polite"
      className={styles.visuallyHidden}
    >
      {announcement}
    </div>
  );
});

function summarizeAssistant(message: AskRedoUIMessage): string {
  const parts = message.parts ?? [];
  for (const part of parts) {
    if (part.type === "text" && part.text.length > 0) {
      const trimmed = part.text.slice(0, ANNOUNCE_PREVIEW_CHARS);
      return `Assistant replied: ${trimmed}`;
    }
  }
  return "Assistant finished.";
}

/**
 * "Thinking" indicator shown the moment a message is sent, before
 * the first assistant chunk arrives. Pairs a slowly-rotating orange
 * AI star with a single playful phrase picked from the shared
 * `AI_THINKING_PHRASES` list (also used by the legacy chat thread).
 */
const ThinkingIndicator = memo(function ThinkingIndicator() {
  const phrase = useMemo(() => {
    const i = Math.floor(Math.random() * AI_THINKING_PHRASES.length);
    return AI_THINKING_PHRASES[i];
  }, []);
  return (
    <div className={styles.thinking}>
      <AskRedoAiStarSpinner />
      <Text fontSize="xs" textColor="secondary">
        {phrase}…
      </Text>
    </div>
  );
});
